/**
 * Dati del Lead/Cliente collegato a una conversazione per il pannello
 * assistente (WhatsApp, Instagram, Messenger): scheda, offerte, progetti e
 * Task commerciali (sales_activities) con creazione, modifica ed eliminazione.
 * Spostato da UnifiedRightPanel per riusarlo nel pannello Instagram/Messenger.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useClientStore } from '@/lib/stores/clientStore';
import { useDealStore } from '@/lib/stores/dealStore';
import { useProjectStore } from '@/lib/stores/projectStore';
import { useCalendarStore } from '@/lib/stores/calendarStore';
import { useApp } from '@/components/AppProvider';
import { supabase } from '@/lib/supabase';
import type { SalesActivity } from '@/lib/types';

interface Params {
  clientId: string | null;
  organizationId: string | null | undefined;
  userId: string | null | undefined;
  /** Il pannello e' visibile: senza, niente letture. */
  enabled: boolean;
  /** Cambia quando cambia la conversazione (ricarica i dati). */
  conversationKey: string;
}

export function useClientPanelData({ clientId, organizationId, userId, enabled, conversationKey }: Params) {
  const { toast } = useApp();
  const selectedClient = useClientStore((s) => s.selectedClient);
  const fetchClientById = useClientStore((s) => s.fetchClientById);
  const deals = useDealStore((s) => s.deals);
  const fetchDeals = useDealStore((s) => s.fetchDeals);
  const projects = useProjectStore((s) => s.projects);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const [activities, setActivities] = useState<SalesActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const client = clientId && selectedClient?.id === clientId ? selectedClient : null;

  const loadActivities = useCallback(async () => {
    if (!clientId) return;
    setActivitiesLoading(true);
    try {
      const { data } = await supabase
        .from('sales_activities')
        .select('*, users:user_profiles!sales_activities_user_id_fkey1(id, email, full_name, profile_image_url)')
        .or(`lead_id.eq.${clientId},client_id.eq.${clientId}`)
        .order('due_date', { ascending: false }).limit(20);
      if (data) setActivities(data);
    } catch (e) { console.error('Error loading activities:', e); }
    finally { setActivitiesLoading(false); }
  }, [clientId]);

  useEffect(() => {
    if (!clientId) setActivities([]);
    if (!enabled || !clientId) return;
    fetchClientById(clientId);
    fetchDeals();
    fetchProjects();
    loadActivities();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, clientId, conversationKey]);

  const createActivity = useCallback(async (activity: Partial<SalesActivity>) => {
    try {
      const { error } = await supabase.from('sales_activities').insert({ ...activity, client_id: clientId, user_id: userId, organization_id: organizationId }).select().single();
      if (error) throw error;
      // L'evento workflow 'lead:activity_created' qui era chiamato con una firma
      // sbagliata e non partiva mai: tolto per non cambiare il comportamento.
      // Accenderlo (emitEvent({ type, payload })) e' una decisione da prendere.
      toast.success('Task commerciale creato'); loadActivities();
    } catch { toast.error('Non sono riuscito a creare il Task commerciale'); }
  }, [clientId, userId, organizationId, loadActivities, toast]);

  const updateActivity = useCallback(async (activityId: string, updates: Partial<SalesActivity>) => {
    try {
      const { error } = await supabase.from('sales_activities').update(updates).eq('id', activityId);
      if (error) throw error;
      toast.success('Task commerciale aggiornato'); loadActivities();
    } catch { toast.error('Non sono riuscito ad aggiornare il Task commerciale'); }
  }, [loadActivities, toast]);

  const deleteActivity = useCallback(async (activityId: string) => {
    try {
      // Bridge delete evento Google collegato: senza questo l'evento resta orfano
      // su Google (stesso pattern di calendarStore.deleteEvent).
      const { data: activityRow } = await supabase
        .from('sales_activities')
        .select('google_event_id, calendar_id')
        .eq('id', activityId)
        .maybeSingle();

      const { error } = await supabase.from('sales_activities').delete().eq('id', activityId);
      if (error) throw error;

      if (activityRow?.google_event_id && activityRow?.calendar_id) {
        try {
          await useCalendarStore.getState().deleteGoogleEvent(activityRow.google_event_id, activityRow.calendar_id);
        } catch (gErr) {
          console.warn('[useClientPanelData] Google delete bridge failed:', gErr);
        }
      }

      toast.success('Task commerciale eliminato'); setActivities((prev) => prev.filter((a) => a.id !== activityId));
    } catch { toast.error('Non sono riuscito a eliminare il Task commerciale'); }
  }, [toast]);

  const clientDeals = useMemo(() => (clientId ? deals.filter((d) => d.client_id === clientId) : []), [deals, clientId]);
  const clientProjects = useMemo(() => (clientId ? projects.filter((p) => p.client_id === clientId) : []), [projects, clientId]);

  return { client, clientDeals, clientProjects, activities, activitiesLoading, loadActivities, createActivity, updateActivity, deleteActivity };
}

export type ClientPanelData = ReturnType<typeof useClientPanelData>;
